import { useRef } from "react";
import PropTypes from "prop-types";

import Button from "../Button";
import Avatar from "../Avatar";
import FieldLabel from "../FieldLabel";
import { UploadSolidV6 as UploadIcon } from "../Icomoon";

import { useGivenOrGeneratedId } from "../../hookHelpers";

const ProfilePhotoUpload = ({ id: idFromProps, alt, label, src, onUpload, onDelete }) => {
  const id = useGivenOrGeneratedId("profile-photo-input", idFromProps);

  const inputRef = useRef(null);

  const onKeyDown = (e) => {
    const { key } = e;

    if (key === "Space" || key === "Enter") {
      inputRef.current.click();
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-8px tw-w-fit">
      <FieldLabel label={label} />
      <div className="tw-flex tw-items-center tw-gap-24px tw-p-16px tw-border-dashed tw-border-1px tw-rounded-2px tw-border-neutral-gray-30">
        <div
          className="tw-group tw-rounded-full"
          onClick={() => inputRef.current.click()}
          role="button"
          tabIndex={0}
          onKeyDown={onKeyDown}
          aria-label="Upload"
        >
          <Avatar size="extra-large" alt={alt} src={src} className="group-hover:tw-hidden" />
          <div
            className={`
              tw-w-96px tw-h-96px tw-border tw-border-solid tw-border-neutral-gray-15
              active:tw-border-theme-primary-alternative active:tw-bg-theme-primary-alternative
              hover:tw-border-theme-primary-alternative hover:tw-bg-white tw-hidden
              group-hover:tw-flex tw-justify-center tw-items-center tw-rounded-full
              tw-overflow-hidden tw-bg-neutral-gray-10 tw-text-neutral-gray-75
            `}
          >
            <UploadIcon className="tw-text-theme-primary-alternative group-active:tw-text-white" size="xxl" />
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-8px tw-items-center">
          <Button
            size="medium"
            schema={src ? "secondary" : "primary"}
            onClick={() => inputRef.current.click()}
          >
            Upload
          </Button>
          {src && (
            <Button size="small" schema="warning" onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>
      <input
        id={id}
        ref={inputRef}
        type="file"
        className="!tw-hidden"
        accept="image/png, image/jpeg"
        onChange={(e) => onUpload(e.target.files[0])}
        // It is necessary to allow sending the same file after deleting it.
        value=""
      />
    </div>
  );
};

ProfilePhotoUpload.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  src: PropTypes.string,
  alt: PropTypes.string,
  onDelete: PropTypes.func,
  onUpload: PropTypes.func,
};

ProfilePhotoUpload.defaultProps = {
  id: null,
  label: "Profile Photo",
  src: "",
  alt: "",
  onDelete: () => {},
  onUpload: () => {},
};

export default ProfilePhotoUpload;
